import { useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout as AntLayout, Menu, Avatar, Button, Alert, Tag, Tooltip, Space } from 'antd';
import type { MenuProps } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard, ReceiptText, Bell, ListChecks, MoonStar,
  Store, UserPlus, Users, KeyRound, LogOut,
  ShieldCheck, ScrollText, AlertTriangle, FileSignature, ClipboardCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AuthUser } from '../api';
import Logo from './Logo';
import { ROLE_KO } from '../labels';

// 빌드 시점 (사용자가 최신 번들 로드 여부를 사이드바에서 즉시 확인용).
// vite에서 replace 되지 않는 상황 대비: __BUILD_TIME__ define + 폴백.
declare const __BUILD_TIME__: string;
const BUILD_TAG: string = (typeof __BUILD_TIME__ !== 'undefined' && __BUILD_TIME__) || 'dev';

const { Sider, Header, Content } = AntLayout;

type MenuItem = Required<MenuProps>['items'][number];

// 메뉴마다 다른 색 박스 + 흰 아이콘. 활성 상태에서 사이드바 좌측 컬러바 색도 이 그라데이션의 첫 색을 따름.
function IconChip({ Icon, gradient, accent }: { Icon: LucideIcon; gradient: string; accent: string }) {
  return (
    <span
      className="menu-icon-chip"
      style={{ background: gradient, ['--chip-accent' as any]: accent }}
      aria-hidden
    >
      <Icon size={15} strokeWidth={2.3} />
    </span>
  );
}

// 각 메뉴별 그라데이션 + 활성 좌측바 색
const G = {
  green:  { grad: 'linear-gradient(135deg, #43c177 0%, #117a3c 100%)', accent: '#22ab5d' },
  teal:   { grad: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)', accent: '#14b8a6' },
  amber:  { grad: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)', accent: '#f59e0b' },
  indigo: { grad: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)', accent: '#6366f1' },
  violet: { grad: 'linear-gradient(135deg, #c4b5fd 0%, #7c3aed 100%)', accent: '#a78bfa' },
  sky:    { grad: 'linear-gradient(135deg, #7dd3fc 0%, #0284c7 100%)', accent: '#38bdf8' },
  slate:  { grad: 'linear-gradient(135deg, #94a3b8 0%, #475569 100%)', accent: '#64748b' },
  lime:   { grad: 'linear-gradient(135deg, #a3e635 0%, #65a30d 100%)', accent: '#84cc16' },
  pink:   { grad: 'linear-gradient(135deg, #f9a8d4 0%, #be185d 100%)', accent: '#ec4899' },
  red:    { grad: 'linear-gradient(135deg, #fca5a5 0%, #b91c1c 100%)', accent: '#ef4444' },
  rose:   { grad: 'linear-gradient(135deg, #fda4af 0%, #9f1239 100%)', accent: '#f43f5e' },
  orange: { grad: 'linear-gradient(135deg, #fdba74 0%, #c2410c 100%)', accent: '#fb923c' },
};

export default function Layout({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const showPwBanner = !!user.must_change_password && !loc.pathname.startsWith('/change-password');
  const displayName = user.name ?? user.username;
  const initial = (displayName.trim()[0] ?? '?').toUpperCase();

  // 스태프 권한 = 운영 일선용 최소 메뉴 (결제 노티·노티 수신 로그·계정 관리는 admin만).
  const isAdmin = user.role === 'admin';
  const items: MenuItem[] = useMemo(() => ([
    {
      key: 'g-status', type: 'group', label: '현황',
      children: [
        { key: '/', icon: <IconChip Icon={LayoutDashboard} gradient={G.green.grad} accent={G.green.accent} />, label: '대시보드' },
      ],
    },
    {
      key: 'g-settle', type: 'group', label: '정산',
      children: [
        { key: '/settlement/discount', icon: <IconChip Icon={ReceiptText} gradient={G.teal.grad}   accent={G.teal.accent} />,   label: '할인매매 이체정산' },
        { key: '/settlement/realtime', icon: <IconChip Icon={ReceiptText} gradient={G.green.grad}  accent={G.green.accent} />,  label: '실시간 이체정산' },
        { key: '/settlement/verify',   icon: <IconChip Icon={ClipboardCheck} gradient={G.amber.grad} accent={G.amber.accent} />, label: '정산 검증' },
        ...(isAdmin ? [
          { key: '/notifications',     icon: <IconChip Icon={Bell}       gradient={G.amber.grad}  accent={G.amber.accent} />,  label: '결제 수신내역' },
          { key: '/notifications-log', icon: <IconChip Icon={ListChecks} gradient={G.indigo.grad} accent={G.indigo.accent} />, label: '결제 수신 로그' },
        ] : []),
        { key: '/dormant',    icon: <IconChip Icon={MoonStar}    gradient={G.violet.grad} accent={G.violet.accent} />, label: '무매출 알림' },
      ],
    },
    {
      key: 'g-merchant', type: 'group', label: '가맹점',
      children: [
        { key: '/merchants',         icon: <IconChip Icon={Store}    gradient={G.sky.grad}  accent={G.sky.accent} />,  label: '가맹점 관리' },
        { key: '/pending-merchants', icon: <IconChip Icon={UserPlus} gradient={G.lime.grad} accent={G.lime.accent} />, label: '결제가맹점 추가' },
        { key: '/renewals',          icon: <IconChip Icon={FileSignature} gradient={G.violet.grad} accent={G.violet.accent} />, label: '계약 갱신/종료' },
      ],
    },
    ...(isAdmin ? [{
      key: 'g-security', type: 'group' as const, label: '보안',
      children: [
        { key: '/security/sessions', icon: <IconChip Icon={ShieldCheck}    gradient={G.red.grad}    accent={G.red.accent} />,    label: '최근 활동 계정' },
        { key: '/security/events',   icon: <IconChip Icon={ScrollText}     gradient={G.rose.grad}   accent={G.rose.accent} />,   label: '보안 로그' },
        { key: '/security/errors',   icon: <IconChip Icon={AlertTriangle}  gradient={G.orange.grad} accent={G.orange.accent} />, label: '오류 로그' },
      ],
    }] : []),
    {
      key: 'g-config', type: 'group', label: '설정',
      children: [
        ...(isAdmin
          ? [{ key: '/users', icon: <IconChip Icon={Users} gradient={G.pink.grad} accent={G.pink.accent} />, label: '계정 관리' }]
          : []),
        { key: '/change-password', icon: <IconChip Icon={KeyRound} gradient={G.slate.grad} accent={G.slate.accent} />, label: '비밀번호 변경' },
      ],
    },
  ]), [isAdmin]);

  const selectedKey = useMemo(() => {
    const candidates = ['/settlement/discount', '/settlement/realtime', '/settlement/verify',
                        '/notifications-log', '/notifications', '/dormant',
                        '/merchants', '/pending-merchants',
                        '/security/sessions', '/security/events', '/security/errors',
                        '/users', '/change-password'];
    for (const c of candidates) if (loc.pathname.startsWith(c)) return c;
    return '/';
  }, [loc.pathname]);

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        width={256}
        theme="light"
        style={{
          borderRight: '1px solid var(--border)',
          position: 'sticky', top: 0, height: '100vh',
          overflow: 'auto',
          background: 'transparent',
        }}
        className="app-sider"
      >
        {/* 사이드바 장식: 상단 블롭 */}
        <div className="sider-bg" aria-hidden>
          <svg viewBox="0 0 256 220" preserveAspectRatio="xMidYMin slice" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="g1" cx="30%" cy="20%" r="80%">
                <stop offset="0%"  stopColor="#22ab5d" stopOpacity="0.22" />
                <stop offset="60%" stopColor="#22ab5d" stopOpacity="0.05" />
                <stop offset="100%" stopColor="#22ab5d" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="g2" cx="80%" cy="0%" r="60%">
                <stop offset="0%"  stopColor="#14b8a6" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect width="256" height="220" fill="url(#g1)" />
            <rect width="256" height="220" fill="url(#g2)" />
            <circle cx="60"  cy="40"  r="42" fill="#22ab5d" opacity="0.08" />
            <circle cx="200" cy="-10" r="56" fill="#14b8a6" opacity="0.08" />
          </svg>
        </div>

        <div className="brand-area">
          <Logo size={30} />
          <div className="brand-text">
            <div className="brand-title">서포트리</div>
            <div className="brand-sub">정산 관리자
              <span title="현재 로드된 화면 빌드 시각 — 강제 새로고침(Ctrl+Shift+R)으로 최신 반영 여부 확인"
                    style={{ display: 'block', fontSize: 10, color: 'var(--text-muted, #94a3b8)', marginTop: 2 }}>
                v {BUILD_TAG}
              </span>
            </div>
          </div>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={items}
          onClick={({ key }) => { if (typeof key === 'string' && key.startsWith('/')) navigate(key); }}
          style={{ borderRight: 0, padding: '4px 4px 24px', background: 'transparent' }}
        />
      </Sider>

      <AntLayout>
        <Header className="app-header">
          <div className="header-deco" aria-hidden />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
            <Tag color={user.role === 'admin' ? 'green' : 'default'} bordered={false} style={{ margin: 0, fontWeight: 600 }}>
              {ROLE_KO[user.role]}
            </Tag>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {dayString()}
            </span>
          </div>
          <Space size={10} style={{ position: 'relative' }}>
            <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
              {displayName}{user.position ? ` ${user.position}` : ''}
            </span>
            <Tooltip title={user.username} placement="bottom">
              <Avatar
                style={{
                  background: 'linear-gradient(135deg, #2e9558 0%, #117a3c 100%)',
                  fontWeight: 700, fontSize: 14, boxShadow: '0 2px 6px -2px rgba(17,122,60,.45)',
                }}
                size={34}
              >
                {initial}
              </Avatar>
            </Tooltip>
            <Button
              icon={<LogOut size={14} />}
              onClick={onLogout}
              size="middle"
            >
              로그아웃
            </Button>
          </Space>
        </Header>

        <Content style={{ padding: '28px 36px 56px', overflowX: 'auto' }}>
          {showPwBanner && (
            <Alert
              type="warning"
              showIcon
              message={
                <span>
                  임시 비밀번호로 로그인 중입니다.{' '}
                  <NavLink to="/change-password" style={{ fontWeight: 600 }}>비밀번호를 변경</NavLink>해 주세요.
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={loc.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Content>
      </AntLayout>
    </AntLayout>
  );
}

function dayString(): string {
  const d = new Date();
  const kst = new Date(d.getTime() + 9 * 3600 * 1000);
  const y = kst.getUTCFullYear();
  const mo = kst.getUTCMonth() + 1;
  const da = kst.getUTCDate();
  const dow = ['일','월','화','수','목','금','토'][kst.getUTCDay()];
  return `${y}년 ${mo}월 ${da}일 (${dow})`;
}
